import DataTable, {type DataTableRef} from 'datatables.net-react'
import 'datatables.net-dt/css/dataTables.dataTables.css';
import DT, {type Config} from 'datatables.net';
import {formattedDate} from "../../util/ReplaceText.tsx";
import type {Port} from "../../lib/ModelTypes.ts";
import React, {forwardRef, type Ref, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import { getDatatableLanguagePack } from "./DatatableLanguagePack.tsx";
import {network, PortForwardingAPIConnectorError } from "../../lib/Network.ts";
import {swal_util} from "../../lib/SwalUtil.ts";
import {instance_manager} from "../../lib/InstanceManager.ts";
import {useTranslation} from "react-i18next";


export interface PortDataRef {
    getSelectedData: () => Port[];
    redraw: () => void;
    onClickDelete: (port: Port) => void;
}

type DataTableProps = {
    ref?: Ref<PortDataRef>;
};

const PortForwardingTable = forwardRef<PortDataRef, DataTableProps>((_props, ref) => {
    const { t, i18n } = useTranslation("ui");

    DataTable.use(DT);
    const dataTableRef = useRef<DataTableRef>(null);

    const [refreshTime, setRefreshTime] = useState(new Date());
    const [data, setData] = useState<{
        port: Port[],
        snapshotId: number,
        snapshotName: string,
        checked: { [key: number]: boolean }
        dataLoading: boolean
    }>({
        port: [],
        snapshotId: 0,
        snapshotName: "",
        checked: {},
        dataLoading: true,
    });

    useImperativeHandle(ref, () => ({
        getSelectedData: () => data.port.filter((row) => data.checked[row.id]),
        redraw: () => redraw().finally(),
        onClickDelete: (port: Port) => onClickDelete(port),
    }));

    const redraw = useCallback(async () => {
        await network.getPortList();
        setData(prev => ({...prev, port: network.ports}));
        setData(prev => ({...prev, dataLoading: false}));
        setRefreshTime(new Date());
    }, []);

    const onClickDelete = useCallback(
        async (port: Port) => {
            const confirm = await swal_util.show_question(
                t("portForwarding.deleteTitle", "포트 포워딩 삭제"),
                t("portForwarding.deleteConfirm", "다음 규칙을 삭제하시겠습니까?")
            );
            if (!confirm.isConfirmed) return;

            try {
                swal_util.show_loading(t("common.deleting", "삭제 중입니다..."));
                await network.deletePort(port.server_id, port.proto, port.intern_port, port.id);
                await redraw();
                await swal_util.hide_loading();
                await swal_util.show_success(t("common.deleted", "삭제되었습니다."));
            } catch (err) {
                try {
                    await swal_util.hide_loading();
                } catch {
                    /* ignore */
                }

                if (err instanceof PortForwardingAPIConnectorError) {
                    await swal_util.show_error(`${err.localization}`);
                } else {
                    await swal_util.show_error(
                        t("portForwarding.errors.deleteFailedContactAdmin", "삭제 중 오류가 발생했습니다. 관리자에게 문의하세요.")
                    );
                }
                console.error(err);
            }
        },
        [t, redraw]
    );

    function handleSelectAll(e: React.UIEvent) {
        const table = dataTableRef.current?.dt();
        if (!table) return;

        const container = table.table().container() as HTMLElement;
        const targetChecked = (e.target as HTMLInputElement).checked;

        const checkboxes = container.querySelectorAll<HTMLInputElement>(".check-port");
        checkboxes.forEach((cb) => {
            cb.checked = targetChecked;
            const id = Number(cb.dataset.id);
            handleCheckboxChange(id, targetChecked);
        });
    }

    function handleCheckboxChange(id: number, checked: boolean) {
        setData((prevData) => ({
            ...prevData,
            checked: {
                ...prevData.checked,
                [id]: checked,
            },
        }));
    }

    const columns = [
        {
            data: "id",
            orderable: false,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            render: (portId: number) => {
                const checked = data.checked[portId];
                return `<input type="checkbox" class="check-port form-check-input" data-id="${portId}" ${checked ? "checked" : ""} />`;
            },
        },
        {
            data: "id",
            render: (_id: number, _type: string, row: Port) => {
                return instance_manager.getInstanceName(row.server_id) ?? t("common.deletionSuccess", "인스턴스");
            },
        },
        {render: () => "pf.blueshift.xslab.co.kr"},
        {data: 'proto'},
        {data: 'intern_port', className: "align-center"},
        {data: 'extern_port', className: "align-center"},
        {
            data: "leased",
            render: (value: string) => formattedDate(value),
        },
        {
            data: 'id',
            render: () => {
                return `<button class="btn btn-danger delete" style="font-size: 14px">${t("common.delete")}</button>`;
            },
            orderable: false,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];

    const options: Config = {
        destroy: true,
        language: getDatatableLanguagePack(i18n.resolvedLanguage),
        paging: false,
        searching: false,
        order: [[1, "asc"]],
        ordering: true,
        lengthChange: false,
        info: false,
    }

    useEffect(() => {
        // 화면이 다시 그려질때 dom을 바인딩하여야 react가 정상 동작
        // option 의 createdRow를 사용하면 안됨
        const tableNodeApi = dataTableRef.current?.dt();
        if (!tableNodeApi) return;

        const container = tableNodeApi.table().container() as HTMLElement;

        const checkboxes = container.querySelectorAll<HTMLInputElement>(".check-port");
        const cbListeners: Array<{ el: HTMLInputElement; fn: (e: Event) => void }> = [];

        checkboxes.forEach((cb) => {
            const fn = (e: Event) => {
                const el = e.currentTarget as HTMLInputElement;
                const id = Number(el.dataset.id);
                handleCheckboxChange(id, el.checked);
            };
            cb.addEventListener("change", fn);
            cbListeners.push({ el: cb, fn });
        });

        const rowsApi = tableNodeApi.rows;
        if (!rowsApi) return;
        const count = rowsApi().count();
        const data: Port[] = tableNodeApi.data().toArray();
        const delListeners: Array<{ el: Element; fn: () => void }> = [];

        for (let i = 0; i < count; i++) {
            const cell = tableNodeApi.cell(i, 7).node();
            const deleteButton = cell.querySelector(".delete");
            if (!deleteButton) continue;

            const fn = () => onClickDelete(data[i]);
            deleteButton.addEventListener("click", fn as EventListener);
            delListeners.push({ el: deleteButton, fn });
        }

        return () => {
            cbListeners.forEach(({ el, fn }) => el.removeEventListener("change", fn));
            delListeners.forEach(({ el, fn }) => el.removeEventListener("click", fn as EventListener));
        };
    }, [onClickDelete, t, refreshTime]);

    return (
        <div style={{ fontSize: "14px" }}>
            <DataTable
                key={`dt-${i18n.resolvedLanguage}`}
                ref={dataTableRef}
                data={data.port}
                columns={columns}
                className="display"
                options={options}
            >
                <thead>
                <tr>
                    <th className="text-center"><input className="form-check-input" type={"checkbox"}
                                                       onClick={(e) => handleSelectAll(e)}/></th>
                    <th>{t("instance.instanceName")}</th>
                    <th>{t("portForwarding.address")}</th>
                    <th>{t("portForwarding.protocol")}</th>
                    <th>{t("portForwarding.internalPort")}</th>
                    <th>{t("portForwarding.externalPort")}</th>
                    <th>{t("portForwarding.leased")}</th>
                    <th>{t("common.action")}</th>
                </tr>
                </thead>
                <tfoot>
                <tr>
                    <td colSpan={columns.length} className="text-start text-secondary border-bottom">
                        {data.dataLoading ? (
                            <span>{t("common.loadingList", "목록을 불러오는 중입니다.")}</span>
                        ) : (
                            <span>
                  {data.port.length} {t("portForwarding.portCount", "개의 포트 규칙")}
                </span>
                        )}
                    </td>
                </tr>
                </tfoot>
            </DataTable>
        </div>
    );
});

export default PortForwardingTable;